import React, { useState } from 'react';
import { useLoaderData } from 'react-router';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import FollowUpAppFooter from '../components/FollowUpAppFooter';
import FollowUpCoworkerPicker from '../components/FollowUpCoworkerPicker';
import RetryMessage from '../components/RetryMessage';
import TimeSlotPicker from '../components/TimeSlotPicker/TimeSlotPicker';
import { StyledWrapper } from '../components/PageLayout';
import useGetAvailableTimeslots from '../hooks/useGetAvailableTimeslots';
import { useStateSelector } from '../store/';
import {
  selectOriginalServiceSlots,
  selectReplacementServiceSlots,
  selectSelectedTimeSlot,
} from '../reducers/selectors';
import type { FollowUpPageData } from '../routers/loaders/followUpLoaderHelper';
import { translationExists } from '../helpers/translationsHelper';

const FollowUpTimeSlotsPage: React.FC = () => {
  const { t } = useTranslation();
  const { serviceId } = useLoaderData<FollowUpPageData>();
  const [sameCoworker, setSameCoworker] = useState(true);
  const selectedTimeSlot = useStateSelector(selectSelectedTimeSlot);
  const slots = useStateSelector(selectOriginalServiceSlots);
  const replacementSlots = useStateSelector(selectReplacementServiceSlots);
  const from = useStateSelector((state) => state.timeSlots.from);
  const prevEnabled = useStateSelector((state) => state.timeSlots.prevEnabled);
  const isLoading = useStateSelector((state) => state.timeSlots.isLoading);
  const showRetry = useStateSelector((state) => state.timeSlots.showRetry);
  const { onRetry, handlePendingSlotSelected } = useGetAvailableTimeslots();
  const serviceName = translationExists(`services.name.${serviceId}`, t)
    ? t(`services.name.${serviceId}`)
    : serviceId;
  return (
    <>
      <PageTitle title={t('pages.followup.timeslots.pageTitle')} />
      <StyledWrapper $withStickyFooter={false}>
        <PageContent>
          <Text tagName="h2" textStyle="Heading.L">
            {serviceName}
          </Text>

          <FollowUpCoworkerPicker
            sameCoworker={sameCoworker}
            onChange={setSameCoworker}
            isDisabled={isLoading}
          />

          {showRetry ? (
            <RetryMessage
              message={t('pages.timeslots.errorMessage')}
              onRetry={onRetry}
            />
          ) : (
            <TimeSlotPicker
              slots={slots}
              replacementServiceSlots={sameCoworker ? [] : replacementSlots}
              shouldStartOn={from}
              onPendingSlotSelected={handlePendingSlotSelected}
              onNextClick={onRetry}
              onPrevClick={onRetry}
              prevEnabled={prevEnabled}
              disabled={isLoading}
            />
          )}

          <FollowUpAppFooter
            prevVisible
            nextVisible
            nextDisabled={!selectedTimeSlot || isLoading}
            isLoading={isLoading}
          />
          {/* TODO: Filter time-window requests by coworkerId when sameCoworker is selected. */}
          {/* TODO: Reserve the selected time window before navigating to the next step. */}
        </PageContent>
      </StyledWrapper>
    </>
  );
};

export default FollowUpTimeSlotsPage;
